'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useVehicleSearch');

export interface Vehicle {
  id: string;
  title: string;
  registration: string | null;
  fuel_type: string | null;
  external_id: string | null;
}

interface UseVehicleSearchParams {
  isVehicleExpense: boolean;
}

export function useVehicleSearch({ isVehicleExpense }: UseVehicleSearchParams) {
  const [allVehicles, setAllVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [showVehicleSearch, setShowVehicleSearch] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const vehicleDropdownRef = useRef<HTMLDivElement>(null);

  const loadVehicles = useCallback(async () => {
    if (allVehicles.length > 0) return;
    setLoadingVehicles(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        '/api/v1/work-items/fleet',
        { headers: { Authorization: `Bearer ${token || ''}` } }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data?.vehicles)) {
        setAllVehicles(
          data.data.vehicles.map((v: { id?: string; title?: string; external_id?: string | null; custom_fields?: { registration?: string | null; fuel_type?: string | null } }) => ({
            id: String(v.id || ''),
            title: v.title || '',
            registration: v.custom_fields?.registration || null,
            fuel_type: v.custom_fields?.fuel_type || null,
            external_id: v.external_id || null,
          }))
        );
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useVehicleSearch] Failed to load vehicles:', error);
    } finally {
      setLoadingVehicles(false);
    }
  }, [allVehicles.length]);

  // Auto-load vehicles when vehicle expense is selected
  useEffect(() => {
    if (isVehicleExpense && allVehicles.length === 0) {
      void loadVehicles();
    }
  }, [isVehicleExpense, allVehicles.length, loadVehicles]);

  const filteredVehicles = useMemo(() => {
    if (!vehicleSearchQuery.trim()) return allVehicles;
    const q = vehicleSearchQuery.toLowerCase();
    return allVehicles.filter(
      (v) =>
        (v.registration && v.registration.toLowerCase().includes(q)) ||
        (v.title && v.title.toLowerCase().includes(q))
    );
  }, [allVehicles, vehicleSearchQuery]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (vehicleDropdownRef.current && !vehicleDropdownRef.current.contains(event.target as Node)) {
        setShowVehicleSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const reset = useCallback(() => {
    setSelectedVehicle(null);
    setVehicleSearchQuery('');
    setShowVehicleSearch(false);
    setAllVehicles([]);
    setLoadingVehicles(false);
  }, []);

  return {
    allVehicles,
    selectedVehicle,
    setSelectedVehicle,
    vehicleSearchQuery,
    setVehicleSearchQuery,
    showVehicleSearch,
    setShowVehicleSearch,
    loadingVehicles,
    filteredVehicles,
    vehicleDropdownRef,
    reset,
  };
}
